import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cx } from './cx';

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  icon?: LucideIcon;
  /** Pastille de compteur ; `late` = rouge (retard), `todo` = orange (à faire). */
  count?: number;
  severity?: 'late' | 'todo';
}

interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Contenu aligné à droite dans la barre (raccourci, bouton…). */
  end?: ReactNode;
  label?: string;
  className?: string;
  /** Identifiant du panneau affiché (`role="tabpanel"`) : relie chaque onglet à son contenu. */
  panelId?: string;
}

/** Barre d'onglets flottante (pilule blanche, onglet actif foncé). */
export function Tabs<T extends string>({ items, value, onChange, end, label = 'Onglets', className, panelId }: TabsProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const listRef = useRef<HTMLDivElement>(null);

  // Petits écrans : la barre défile horizontalement ; l'onglet actif reste visible.
  const activeIndex = items.findIndex((tab) => tab.id === value);
  useEffect(() => {
    const list = listRef.current;
    const tab = refs.current[activeIndex];
    if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
    const left = tab.offsetLeft - list.offsetLeft;
    if (left < list.scrollLeft || left + tab.offsetWidth > list.scrollLeft + list.clientWidth)
      list.scrollTo({ left: Math.max(0, left - 16), behavior: 'smooth' });
  }, [activeIndex]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = items.length - 1;
    const next =
      event.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
      : event.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
      : event.key === 'Home' ? 0
      : event.key === 'End' ? last
      : null;
    if (next === null) return;
    event.preventDefault();
    onChange(items[next].id);
    refs.current[next]?.focus();
  };

  return (
    <div
      className={cx(
        'flex items-center gap-2 rounded-panel border border-line bg-white p-1.5 shadow-navbar',
        className,
      )}
    >
      <div ref={listRef} role="tablist" aria-label={label} className="flex gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((tab, index) => {
          const on = tab.id === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                refs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={panelId ? `${panelId}-tab-${tab.id}` : undefined}
              aria-controls={panelId && on ? panelId : undefined}
              aria-selected={on}
              // La pastille reste hors du nom accessible (« Finance », pas « Finance 2 en retard ») :
              // elle est annoncée comme description.
              aria-description={
                tab.count != null && tab.count > 0
                  ? `${tab.count} ${tab.severity === 'late' ? 'en retard' : 'à faire'}`
                  : undefined
              }
              tabIndex={on ? 0 : -1}
              onClick={() => onChange(tab.id)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={cx(
                'inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-3xl px-[18px] text-sm font-medium transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100',
                on ? 'bg-heading text-white' : 'text-secondary hover:bg-surface-light hover:text-heading',
              )}
            >
              {Icon && <Icon size={16} strokeWidth={1.9} aria-hidden />}
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span
                  className={cx(
                    'grid h-[18px] min-w-[18px] place-items-center rounded-[9px] px-[5px] text-[10px] font-bold',
                    on ? 'bg-white/20 text-white'
                    : tab.severity === 'late' ? 'bg-danger-soft text-danger-dark'
                    : 'bg-warn-bg text-warn-fg',
                  )}
                  aria-hidden
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {end && <div className="ml-auto flex items-center gap-1.5 pr-0.5">{end}</div>}
    </div>
  );
}

interface SegmentedProps<T extends string> {
  options: Array<{ id: T; label: string; hint?: string; icon?: LucideIcon }>;
  value: T;
  onChange: (id: T) => void;
  full?: boolean;
  label?: string;
  /** Icônes seules : le libellé devient l'info-bulle et le nom accessible. */
  iconOnly?: boolean;
}

/** Sélecteur segmenté (filtre à 2–4 choix). La pastille blanche glisse vers l'option choisie. */
export function Segmented<T extends string>({ options, value, onChange, full = false, label, iconOnly = false }: SegmentedProps<T>) {
  const rail = useRef<HTMLDivElement>(null);
  // Position de la pastille (mesurée sur le bouton choisi). Tant qu'elle n'est pas mesurée, le bouton
  // choisi porte lui-même le fond blanc ; la première mesure se pose sans animation.
  const [pill, setPill] = useState<{ left: number; width: number; animate: boolean } | null>(null);
  useLayoutEffect(() => {
    const el = rail.current;
    if (!el) return;
    const measure = () => {
      const button = el.querySelector<HTMLElement>('[aria-checked="true"]');
      if (!button) return setPill(null);
      setPill((prev) => {
        const next = { left: button.offsetLeft, width: button.offsetWidth };
        if (prev && prev.left === next.left && prev.width === next.width) return prev;
        return { ...next, animate: !!prev };
      });
    };
    measure();
    // Largeurs qui changent (police chargée, fenêtre redimensionnée) : nouvelle mesure.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [value, options.length]);

  return (
    <div
      ref={rail}
      role="radiogroup"
      aria-label={label}
      className={cx(
        // Rail gris un peu plus soutenu que le fond des pages, pour que la pastille blanche ressorte.
        'relative gap-0.5 rounded-pill bg-[#E9EDF3] p-[3px]',
        full ? 'flex' : 'inline-flex',
      )}
    >
      {pill && (
        <span
          aria-hidden
          className={cx(
            'pointer-events-none absolute top-[3px] h-[30px] rounded-pill bg-white shadow-navbar',
            pill.animate && 'transition-[left,width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
          )}
          style={{ left: pill.left, width: pill.width }}
        />
      )}
      {options.map((option) => {
        const on = option.id === value;
        const Icon = option.icon;
        const bare = iconOnly && !!Icon;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={bare ? option.label : undefined}
            title={bare ? option.label : undefined}
            onClick={() => onChange(option.id)}
            className={cx(
              'relative z-[1] inline-flex h-[30px] items-center justify-center gap-1.5 rounded-pill text-[13px] font-medium transition-colors duration-200',
              bare ? 'w-[34px]' : 'px-3.5',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200',
              full && 'flex-1',
              on ? cx('text-heading', !pill && 'bg-white shadow-navbar') : 'text-secondary hover:text-heading',
            )}
          >
            {Icon && <Icon size={15} strokeWidth={1.9} aria-hidden />}
            {!bare && option.label}
            {option.hint && <small className="text-[11px] font-semibold text-muted">{option.hint}</small>}
          </button>
        );
      })}
    </div>
  );
}
